// Auth client (docs/03 §3, §3a): register, challenge–response login, rotating
// refresh, and password login for additional sessions. All request/response
// shapes come from @merkury/protocol; responses are zod-validated before use.
//
// Signature message layouts (domain-separated, docs/03 §3/§3b):
//   auth:   utf8("merkury-auth-v1")   || raw(nonce) || utf8(userId)
//   delete: utf8("merkury-delete-v1") || utf8(userId) || raw(nonce)
// where raw(nonce) is the base64-decoded server nonce. The Go server (J4)
// must assemble the identical bytes.

import type { z } from 'zod';
import {
  ApiError as ApiErrorSchema,
  AuthChallengeResponse,
  AuthVerifyResponse,
  PasskeyLoginOptionsResponse,
  PasskeyLoginResponse,
  PasswordLoginResponse,
  RefreshResponse,
  RegisterResponse,
  SIG_DOMAIN,
  type ErrorCode,
  type RegisterRequest,
  type SessionKind,
  type SetPasswordRequest,
  type TokenBundle,
} from '@merkury/protocol';
import { concatBytes, fromBase64, toBase64, utf8 } from '@merkury/brain/lib/bytes.js';
import { defaultFetch } from '@merkury/brain/lib/http.js';
import {
  PRF_RESTORE_SALT,
  decryptIdentitySeed,
  derivePasskeyWrapKey,
  deriveSplitKeys,
  encryptIdentitySeed,
  generateKdfSalt,
  type Argon2idProvider,
} from '@merkury/brain/identity/kdf.js';
import { getAssertion, getAssertionWithPrf, supportsWebAuthn } from './webauthn.js';
import { keysFromSeed, signWithIdentity, type IdentityKeys } from '@merkury/brain/identity/keys.js';

export function buildAuthMessage(nonce: Uint8Array, userId: string): Uint8Array {
  return concatBytes(utf8(SIG_DOMAIN.AUTH), nonce, utf8(userId));
}

export function buildDeleteMessage(userId: string, nonce: Uint8Array): Uint8Array {
  return concatBytes(utf8(SIG_DOMAIN.DELETE), utf8(userId), nonce);
}

/**
 * Thrown when a key login (challenge/verify) needs the account password too
 * (2FA, docs/03 §3a) and no correct `authKey` was supplied. Distinct from a
 * generic `ApiError` so the onboarding restore flow can catch exactly this and
 * prompt for the password. Carries the userId so the caller can retry.
 */
export class PasswordRequiredError extends Error {
  readonly userId: string;
  constructor(userId: string) {
    super('password required');
    this.name = 'PasswordRequiredError';
    this.userId = userId;
  }
}

/** REST error with the protocol `ErrorCode` when the server supplied one. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | undefined;

  constructor(status: number, code: ErrorCode | undefined, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Whether a refresh failure really means "this session is over", as opposed to
 * something between us and the server saying no. Gates only the destructive
 * half of the failure path — recovery is attempted on every 401/403.
 *
 * The server sets a protocol `code` on EVERY error it emits (httpapi/respond.go
 * writeError is the only path), so a 401/403 carrying no code did not come from
 * the server: it is a Cloudflare challenge page, a captive portal, or — in this
 * app's actual threat model — an interceptor. Treating those as authoritative
 * made the client delete its stored refresh token and mint a brand-new session
 * on every intercepted refresh, filling the user's session list with phantom
 * devices and discarding the resume state each time. When in doubt, keep the
 * session: a refresh can always be retried, but a deleted token cannot.
 */
function isSessionDeath(error: ApiError): boolean {
  if (error.status !== 401 && error.status !== 403) return false;
  return error.code !== undefined;
}

/** Parsed passkey login/restore options (server-issued assertion options plus
 *  an optional legacy per-user `prfSalt`), fetched by `passkeyRestoreOptions`
 *  and consumed by `passkeyRestoreWithOptions`. */
export type PasskeyLoginOptions = z.infer<typeof PasskeyLoginOptionsResponse>;

export interface AuthClientOptions {
  /** REST base incl. version prefix, e.g. "/api/v1". No trailing slash. */
  baseUrl: string;
  /**
   * Device kind stamped on every session this client creates (verify /
   * password / passkey login), so the Capacitor app's sessions list as
   * 'android'. The identity layer never imports Capacitor; the shell passes
   * this in (platform seam). Defaults to 'web'.
   */
  sessionKind?: SessionKind;
  /**
   * Argon2id implementation for the split-KDF (password login/2FA). Injected
   * so this module stays WASM-free: web/shell callers pass `webArgon2id`
   * (identity/argon2Web.ts), the headless brain passes the native seam.
   */
  argon2: Argon2idProvider;
  /** Injectable for tests. */
  fetchFn?: typeof fetch;
}

export class AuthClient {
  private readonly baseUrl: string;
  private readonly sessionKind: SessionKind;
  private readonly fetchFn: typeof fetch;
  private readonly argon2: Argon2idProvider;

  constructor(options: AuthClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.sessionKind = options.sessionKind ?? 'web';
    this.argon2 = options.argon2;
    // Bounded + retrying by default (lib/http.ts). Login and refresh are the
    // first calls a cold app makes, so an unbounded one here strands the whole
    // session behind a stalled socket.
    this.fetchFn = options.fetchFn ?? defaultFetch();
  }

  private async post<S extends z.ZodType>(
    path: string,
    body: unknown,
    responseSchema: S,
    accessToken?: string,
  ): Promise<z.infer<S>> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (accessToken !== undefined) headers['authorization'] = `Bearer ${accessToken}`;
    const response = await this.fetchFn(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let code: ErrorCode | undefined;
      let message = `request to ${path} failed with status ${response.status}`;
      try {
        const parsed = ApiErrorSchema.safeParse(await response.json());
        if (parsed.success) {
          code = parsed.data.code;
          message = parsed.data.message;
        }
      } catch {
        // Non-JSON error body — keep the generic message.
      }
      throw new ApiError(response.status, code, message);
    }
    const parsed = responseSchema.safeParse(await response.json());
    if (!parsed.success) {
      throw new ApiError(response.status, undefined, `malformed response from ${path}`);
    }
    return parsed.data;
  }

  /**
   * `POST /register` — first launch, and the restore path: registration is
   * idempotent for a known publicKey (J4 contract), returning the existing
   * userId; the server keeps the account's existing display name.
   */
  register(publicKeyB64: string, displayName: string): Promise<RegisterResponse> {
    const body: RegisterRequest = { publicKey: publicKeyB64, displayName };
    return this.post('/register', body, RegisterResponse);
  }

  /**
   * Challenge–response login with the identity key (docs/03 §3). When the
   * account has a password set (2FA, docs/03 §3a) the server answers
   * `password_required` unless `authKey` — the same password proof
   * `loginWithPassword` sends — is supplied; this method surfaces that as a
   * `PasswordRequiredError` so callers can prompt then retry with `authKey`.
   */
  async loginWithKey(userId: string, seed: Uint8Array, authKey?: Uint8Array): Promise<TokenBundle> {
    const challenge = await this.post('/auth/challenge', { userId }, AuthChallengeResponse);
    const nonce = fromBase64(challenge.nonce);
    const signature = await signWithIdentity(seed, buildAuthMessage(nonce, userId));
    try {
      return await this.post(
        '/auth/verify',
        {
          userId,
          nonce: challenge.nonce,
          signature: toBase64(signature),
          kind: this.sessionKind,
          ...(authKey ? { authKey: toBase64(authKey) } : {}),
        },
        AuthVerifyResponse,
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'password_required') {
        throw new PasswordRequiredError(userId);
      }
      throw error;
    }
  }

  /**
   * Derive the password proof (`authKey`) for `userId` from a typed password:
   * fetch a challenge for its `kdfSalt`, run the split-KDF, return the authKey
   * branch. Used to satisfy the 2FA key-login path and to persist the proof
   * for silent re-login.
   */
  async deriveAuthKey(userId: string, password: string): Promise<Uint8Array> {
    const challenge = await this.post('/auth/challenge', { userId }, AuthChallengeResponse);
    const { authKey } = await deriveSplitKeys(password, fromBase64(challenge.kdfSalt), this.argon2);
    return authKey;
  }

  /**
   * Password login for a new session (docs/03 §3a): fetch a challenge — its
   * `kdfSalt` is how a fresh device learns the salt — derive authKey from the
   * typed password, exchange it for tokens + the encIdentityKey blob, decrypt
   * the identity seed locally with the independent encKey branch.
   *
   * The server returns a deterministic decoy salt for unknown or passwordless
   * accounts, so a wrong attempt fails at `POST /auth/password` — salt fetch
   * itself never fails, and no error path here may assume it does.
   */
  async loginWithPassword(
    userId: string,
    password: string,
  ): Promise<{ tokens: TokenBundle; identity: IdentityKeys }> {
    const challenge = await this.post('/auth/challenge', { userId }, AuthChallengeResponse);
    const { authKey, encKey } = await deriveSplitKeys(password, fromBase64(challenge.kdfSalt), this.argon2);
    const response = await this.post(
      '/auth/password',
      { userId, authKey: toBase64(authKey), kind: this.sessionKind },
      PasswordLoginResponse,
    );
    const seed = decryptIdentitySeed(encKey, fromBase64(response.encIdentityKey));
    const { encIdentityKey: _encIdentityKey, ...tokens } = response;
    return { tokens, identity: keysFromSeed(seed) };
  }

  /** `POST /auth/refresh` — rotating refresh token (docs/03 §3). */
  refresh(refreshToken: string): Promise<TokenBundle> {
    return this.post('/auth/refresh', { refreshToken }, RefreshResponse);
  }

  /**
   * Passkey (WebAuthn) login: mints a SERVER session on this device — it does
   * NOT restore the E2E identity (docs/03 §2). Runs the assertion ceremony
   * against the server-issued options and exchanges the result for tokens. The
   * ceremony runs through the identity/webauthn platform seam: web uses
   * `navigator.credentials.get`, the Capacitor native app routes through the
   * Credential Manager plugin (the WebView itself lacks WebAuthn). Returns
   * undefined when no platform path is available so callers can treat it as
   * "this rescue is unavailable" rather than an error.
   */
  async passkeyLogin(userId: string): Promise<TokenBundle | undefined> {
    if (!supportsWebAuthn()) return undefined;
    const options = await this.post(
      '/auth/passkeys/login/options',
      { userId },
      PasskeyLoginOptionsResponse,
    );
    const credential = await getAssertion(options);
    if (!credential) return undefined;
    const response = await this.post(
      '/auth/passkeys/login',
      { userId, credential, kind: this.sessionKind },
      PasskeyLoginResponse,
    );
    // Session rescue only needs tokens; a passkey-carried seed blob (if any) is
    // irrelevant here (this device already holds the identity).
    const { encIdentityKey: _encIdentityKey, ...tokens } = response;
    return tokens;
  }

  /**
   * Passkey identity RESTORE on a NEW device (docs/03 §2): the modern WebAuthn
   * PRF pattern. Without `userId` this is the DISCOVERABLE (usernameless) flow
   * — nothing typed: the authenticator offers its resident Merkury passkeys,
   * the PRF is evaluated over the fixed app-wide restore salt, the server
   * verifies the assertion (reading the account from its userHandle) and
   * returns the asserted credential's `encIdentityKey` ciphertext, which the
   * client decrypts locally. With `userId` (legacy escape hatch) the account's
   * per-user salt is ALSO evaluated in the same ceremony, so blobs wrapped
   * under the old scheme still restore. Returns undefined (never throws for a
   * missing-capability reason) when: no WebAuthn platform, the ceremony
   * yielded no PRF output, the server returned no ciphertext, or the blob
   * decrypts under neither key.
   */
  async passkeyRestore(
    userId?: string,
  ): Promise<{ tokens: TokenBundle; identity: IdentityKeys } | undefined> {
    const options = await this.passkeyRestoreOptions(userId);
    if (!options) return undefined;
    return this.passkeyRestoreWithOptions(options, userId);
  }

  /**
   * Fetch (only) the assertion options for a passkey restore — the SLOW,
   * network half of `passkeyRestore`, split out so the caller can prefetch it
   * BEFORE the user gesture. Keeping the round-trip off the click path is what
   * lets `navigator.credentials.get` fire inside Safari's transient user
   * activation window (web/passkey-audit.md §1). Returns undefined when no
   * WebAuthn platform exists (so callers treat it as "unavailable", not error).
   */
  async passkeyRestoreOptions(userId?: string): Promise<PasskeyLoginOptions | undefined> {
    if (!supportsWebAuthn()) return undefined;
    return this.post(
      '/auth/passkeys/login/options',
      userId ? { userId } : {},
      PasskeyLoginOptionsResponse,
    );
  }

  /**
   * Run the restore ceremony against ALREADY-FETCHED options — the fast half,
   * with no network await before `navigator.credentials.get` so the gesture's
   * activation survives (see passkeyRestoreOptions). A dismissed sheet or a
   * platform WebAuthn error REJECTS (callers map it to distinguishable copy);
   * resolves undefined only for "ran but nothing restorable" (no PRF output,
   * no server ciphertext, or the blob decrypts under neither key).
   */
  async passkeyRestoreWithOptions(
    options: PasskeyLoginOptions,
    userId?: string,
  ): Promise<{ tokens: TokenBundle; identity: IdentityKeys } | undefined> {
    const ceremony = await getAssertionWithPrf(options, {
      first: PRF_RESTORE_SALT,
      ...(options.prfSalt ? { second: fromBase64(options.prfSalt) } : {}),
    });
    if (!ceremony?.prfOutput) return undefined;
    const response = await this.post(
      '/auth/passkeys/login',
      { ...(userId ? { userId } : {}), credential: ceremony.credential, kind: this.sessionKind },
      PasskeyLoginResponse,
    );
    if (!response.encIdentityKey) return undefined;
    const blob = fromBase64(response.encIdentityKey);
    const seed = this.unwrapSeed(blob, ceremony.prfOutput, ceremony.prfOutputSecond);
    if (!seed) return undefined;
    const { encIdentityKey: _encIdentityKey, ...tokens } = response;
    return { tokens, identity: keysFromSeed(seed) };
  }

  /** Try the fixed-salt wrap key first, then the legacy per-user-salt key —
   *  Poly1305 authentication makes a wrong-key attempt fail loudly, never
   *  yield garbage. */
  private unwrapSeed(
    blob: Uint8Array,
    prfFirst: Uint8Array,
    prfSecond?: Uint8Array,
  ): Uint8Array | undefined {
    for (const output of [prfFirst, prfSecond]) {
      if (!output) continue;
      try {
        return decryptIdentitySeed(derivePasskeyWrapKey(output), blob);
      } catch {
        // Wrong key branch for this blob — try the next.
      }
    }
    return undefined;
  }

  /**
   * Build the `PUT /me/password` body that sets/changes the password
   * (docs/03 §3a, J4 contract): fresh salt, the pw-auth branch output sent
   * as-is in `authHash` (the server hashes it at rest — the same value is
   * later presented as `authKey` on password login), and the seed encrypted
   * under the independent encKey branch. The fresh-signature guard is a
   * challenge nonce signed with the regular auth message (`merkury-auth-v1`,
   * `buildAuthMessage`) — the caller signs and supplies it.
   */
  async buildSetPasswordFields(
    password: string,
    seed: Uint8Array,
  ): Promise<Pick<SetPasswordRequest, 'authHash' | 'kdfSalt' | 'encIdentityKey'>> {
    const kdfSalt = generateKdfSalt();
    const { authKey, encKey } = await deriveSplitKeys(password, kdfSalt, this.argon2);
    return {
      authHash: toBase64(authKey),
      kdfSalt: toBase64(kdfSalt),
      encIdentityKey: toBase64(encryptIdentitySeed(encKey, seed)),
    };
  }
}

interface TokenState {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms after which the access token must be refreshed. */
  expiresAt: number;
  sessionId: string;
}

export interface TokenManagerOptions {
  /** Persist the rotated refresh token (docs/03 §7: secure storage). */
  onRefreshTokenRotated?: (refreshToken: string) => void | Promise<void>;
  /**
   * Last-chance recovery when the server rejects the in-memory refresh token
   * (401/403). Two legitimate rescues exist on device: another actor (the
   * native stay-reachable listener) may have rotated the token in storage
   * under us, or a full challenge/verify key login can mint a fresh session.
   * Return a bundle to keep the session alive; return undefined (or throw)
   * to declare it lost. `failedToken` is the token the server just rejected,
   * so the recoverer can avoid replaying it.
   */
  recoverSession?: (error: unknown, failedToken: string) => Promise<TokenBundle | undefined>;
  /** Called when refresh fails terminally (revoked/expired) — force re-login. */
  onSessionLost?: (error: unknown) => void;
  now?: () => number;
  /** Refresh this many ms before nominal expiry. */
  skewMs?: number;
}

/**
 * Access token in memory only; refresh token handed to the caller's secure
 * storage on every rotation (docs/03 §7). Concurrent callers share one
 * in-flight refresh — rotating refresh tokens make duplicate refreshes a
 * security event (reuse detection kills the session family), not a retry.
 */
export class TokenManager {
  private state: TokenState | undefined;
  private inflight: Promise<TokenBundle> | undefined;
  private readonly client: AuthClient;
  private readonly options: TokenManagerOptions;

  constructor(client: AuthClient, options: TokenManagerOptions = {}) {
    this.client = client;
    this.options = options;
  }

  setTokens(bundle: TokenBundle): void {
    const now = this.options.now?.() ?? Date.now();
    this.state = {
      accessToken: bundle.accessToken,
      refreshToken: bundle.refreshToken,
      expiresAt: now + bundle.expiresIn * 1000,
      sessionId: bundle.sessionId,
    };
  }

  get sessionId(): string | undefined {
    return this.state?.sessionId;
  }

  clear(): void {
    this.state = undefined;
    this.inflight = undefined;
  }

  /** Returns a valid access token, refreshing (single-flight) when stale. */
  async getAccessToken(): Promise<string> {
    if (!this.state) throw new Error('not authenticated');
    const now = this.options.now?.() ?? Date.now();
    const skew = this.options.skewMs ?? 60_000;
    if (now < this.state.expiresAt - skew) return this.state.accessToken;

    this.inflight ??= this.runRefresh(this.state.refreshToken);
    const bundle = await this.inflight; // rejection propagates to every waiter
    return bundle.accessToken;
  }

  /**
   * The whole refresh — network call, state update, rotation persistence,
   * loss handling — lives inside the single-flight promise so side effects
   * fire exactly once however many callers are waiting.
   */
  private async runRefresh(refreshToken: string): Promise<TokenBundle> {
    try {
      const bundle = await this.client.refresh(refreshToken);
      this.setTokens(bundle);
      await this.options.onRefreshTokenRotated?.(bundle.refreshToken);
      return bundle;
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        // Always worth a rescue: the usual cause is another actor (the native
        // reach listener) having rotated the token in storage under us.
        const rescued = await this.tryRecover(error, refreshToken);
        if (rescued) return rescued;
        // Only DESTROY the session when the server actually said so.
        if (isSessionDeath(error)) {
          this.clear();
          this.options.onSessionLost?.(error);
        }
      }
      throw error;
    } finally {
      this.inflight = undefined;
    }
  }

  /** Field-verified failure this rescues: the app sat "offline" (dead WS,
   * failing REST) until a restart, because the native reach listener had
   * rotated the refresh token in storage and the in-memory copy 401'd. */
  private async tryRecover(error: unknown, failedToken: string): Promise<TokenBundle | undefined> {
    if (!this.options.recoverSession) return undefined;
    try {
      const bundle = await this.options.recoverSession(error, failedToken);
      if (!bundle) return undefined;
      this.setTokens(bundle);
      await this.options.onRefreshTokenRotated?.(bundle.refreshToken);
      return bundle;
    } catch {
      return undefined;
    }
  }
}
